import vtkWSLinkClient from "@kitware/vtk.js/IO/Core/WSLinkClient";
import SmartConnect from "wslink/src/SmartConnect";

import { EventsEnum } from "../classes/enums/EventsEnum";
import { RemoteRenderingActionEnum } from "../classes/enums/RemoteRenderingActionEnum";
import { TranslationDirections } from "../classes/enums/SurgicalActsEnum";
import { SpineRegions } from "../classes/enums/ValidationEnums";
import { VertebraSelectionModality } from "../classes/enums/VertebraSelectionEnum";
import { getToken, isTokenExpired } from "../classes/utils/TokenUtils";
import { oidcManager } from "../services/oauth/OAuthService";

class RenderingClient {
  static DEFAULT_ZOOM_SENSITIVITY = 1.0;

  // Private fields
  #config = {
    application: "cone",
    sessionURL: null,
  };
  #viewsList = [];
  #surgeryReloadCallbacks = new Set();
  #wslSession = null;
  #remoteSessionId = null;
  #clientToConnect = null;
  #isConnected = false;
  #isReconnecting = false;
  #disconnecting = false;

  // Retry configuration
  static MAX_RETRY_ATTEMPTS = 3;
  static INITIAL_RETRY_DELAY_MS = 3000;
  static MAX_RETRY_DELAY_MS = 10000;
  #retryAttempts = 0;
  #hasExhaustedRetries = false;
  #connectionErrorCallbacks = new Set();
  #renderingOptions = {
    zoomSensitivity: RenderingClient.DEFAULT_ZOOM_SENSITIVITY,
  };

  // --------------------------------------------------------------------------
  // Constructor & Connection Event Listeners
  // --------------------------------------------------------------------------
  constructor(renderingOptions = {}) {
    // Use environment variable if provided, otherwise fall back to window.location.hostname
    const hostname =
      process.env.REACT_APP_RENDERING_HOSTNAME || window.location.hostname;
    const port = 8787; // Change this if needed
    this.#config.sessionURL = `wss://${hostname}:${port}/ws`;

    // Set up WSLink client
    vtkWSLinkClient.setSmartConnectClass(SmartConnect);
    this.#clientToConnect = vtkWSLinkClient.newInstance();
    this.setRenderingOptions(renderingOptions);

    // When errors or closures occur, mark connection as closed and notify
    // vtkRemoteView instances so they can detach their stale session reference.
    this.#clientToConnect.onConnectionError(httpReq => {
      this.#isConnected = false;
      this.#connectionError(httpReq, "Connection error");
      window.dispatchEvent(new CustomEvent("wslink:disconnected"));
    });
    this.#clientToConnect.onConnectionClose(httpReq => {
      this.#isConnected = false;
      this.#connectionInfo(httpReq, "Connection closed");
      window.dispatchEvent(new CustomEvent("wslink:disconnected"));
    });
  }

  static normalizeZoomSensitivity(value) {
    const parsed =
      typeof value === "string" ? Number.parseFloat(value) : Number(value);

    if (!Number.isFinite(parsed) || parsed <= 0) {
      return RenderingClient.DEFAULT_ZOOM_SENSITIVITY;
    }

    return parsed;
  }

  // --------------------------------------------------------------------------
  // Safe RPC Call Helper
  // --------------------------------------------------------------------------
  #refreshSessionArgs(args, previousSessionId) {
    if (
      !Array.isArray(args) ||
      !previousSessionId ||
      !this.#remoteSessionId ||
      args[0] !== previousSessionId
    ) {
      return args;
    }

    const nextArgs = [...args];
    nextArgs[0] = this.#remoteSessionId;
    return nextArgs;
  }

  async safeRpcCall(method, args) {
    if (!this.#wslSession) {
      if (this.#hasExhaustedRetries) {
        throw new Error(
          `RPC call ${method} failed: connection retries exhausted. Refresh the page to try again.`
        );
      }
      try {
        await this.connectAsync();
      } catch (error) {
        throw new Error(`RPC call ${method} failed: cannot reconnect.`);
      }
    }
    try {
      return await this.#wslSession.call(method, args);
    } catch (error) {
      // If error code -32099 indicates that the connection is closed...
      if (error && error.code === -32099) {
        const previousSessionId = this.#remoteSessionId;
        this.#isConnected = false;
        if (this.#hasExhaustedRetries) {
          throw new Error(
            `Connection lost and retries exhausted. Refresh the page to try again.`
          );
        }
        try {
          await this.connectAsync();
          const retryArgs = this.#refreshSessionArgs(args, previousSessionId);
          return await this.#wslSession.call(method, retryArgs);
        } catch (reconnectError) {
          throw reconnectError;
        }
      } else {
        throw error;
      }
    }
  }

  // --------------------------------------------------------------------------

  // --------------------------------------------------------------------------
  #setupSubscriptions() {
    if (!this.#wslSession) {
      return;
    }

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.UpdateMeasurement,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.UPDATE_MEASUREMENT, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.VertebraSelection,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.VERTEBRA_SELECTION, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.OsteotomyToolManualChange,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(
            EventsEnum.MANUAL_OSTEOTOMY_TOOL_UPDATE,
            {
              detail: eventData,
            }
          );
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.GetMeasurements,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.GET_MEASUREMENTS, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.StartMouseTracking,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.START_MOUSE_TRACKING, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.EndMouseTracking,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.END_MOUSE_TRACKING, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.MousePositionUpdate,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.MOUSE_POSITION_UPDATE, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.UpdateViews,
      message => {
        try {
          const eventData = this.#extractMessageData(message);

          // Log the actual structure to help debug
          const event = new CustomEvent(EventsEnum.UPDATE_VIEWS, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.SegmentalTransfromationToolManualChange,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(
            EventsEnum.MANUAL_SEGMENTAL_TRANSFORMATION_TOOL_UPDATE,
            {
              detail: eventData,
            }
          );
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.CageToolManualChange,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.MANUAL_CAGE_TOOL_UPDATE, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.Toggle_loading,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.TOGGLE_LOADING, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.RadioDensityVertebraSelected,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(
            EventsEnum.RADIO_DENSITY_VERTEBRA_SELECTED,
            {
              detail: eventData,
            }
          );
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );

    this.#wslSession.subscribe(
      RemoteRenderingActionEnum.DrrValidationPointAdded,
      message => {
        try {
          const eventData = this.#extractMessageData(message);
          const event = new CustomEvent(EventsEnum.DRR_VALIDATION_POINT_ADDED, {
            detail: eventData,
          });
          window.dispatchEvent(event);
        } catch (error) {}
      }
    );
  }

  #extractMessageData(message) {
    try {
      if (!message) {
        return null;
      }

      if (Array.isArray(message) && message.length > 0) {
        const firstItem = message[0];
        if (firstItem && firstItem.hasOwnProperty("data")) {
          return [firstItem.data];
        }

        return message;
      }

      if (message.hasOwnProperty("data")) {
        return [message.data];
      }

      if (!Array.isArray(message)) {
        return [message];
      }

      return message;
    } catch (error) {
      return Array.isArray(message) ? message : [message];
    }
  }

  // region Session --------------------------------------------------------------------------
  // Connection Methods
  // --------------------------------------------------------------------------
  async unregisterAllViewStreams() {
    if (!this.#clientToConnect || !this.#clientToConnect.getImageStream()) {
      return;
    }

    const imageStream = this.#clientToConnect.getImageStream();

    // Create a copy of the views list to avoid mutation during iteration
    const viewsToUnregister = [...this.#viewsList];

    // Unregister each view stream sequentially (not in parallel)
    for (const viewData of viewsToUnregister) {
      if (!viewData || !viewData.viewId) continue;

      try {
        // Attempt to unregister the view stream
        await imageStream.unregisterViewStream(viewData.viewId);
      } catch (error) {
        // Continue with other views even if one fails - view may already be unregistered
      }
    }

    // Clear the views list after processing
    this.#viewsList = [];
  }

  async associateClientWithSession(sessionId) {
    try {
      await this.#wslSession.call(
        RemoteRenderingActionEnum.AssociateClientSession,
        [sessionId]
      );
    } catch (error) {
      throw error;
    }
  }

  async connectAsync() {
    if (this.#isConnected) {
      return;
    }

    // Check if retries are exhausted
    if (this.#hasExhaustedRetries) {
      const error = new Error(
        "Connection retries exhausted. Refresh the page to try again."
      );
      error.retriesExhausted = true;
      throw error;
    }

    // Get token and append to URL
    let token = getToken();
    if (!token) {
      throw new Error(
        "No authentication token available for rendering connection"
      );
    }

    // If token is expired, check if UserManager has a fresh one (from automatic renewal)
    if (isTokenExpired(token)) {
      try {
        const user = await oidcManager.getUser();
        if (user && !user.expired) {
          token = user.access_token;
        } else {
          throw new Error("Authentication token has expired and renewal not available");
        }
      } catch (error) {
        console.error('[RenderingClient] Failed to get fresh token:', error);
        throw new Error("Authentication token has expired");
      }
    }

    const authenticatedUrl = `${this.#config.sessionURL}?token=${encodeURIComponent(token)}`;

    try {
      await this.#clientToConnect.disconnect();
    } catch (e) {}

    try {
      await this.#clientToConnect.connect({
        ...this.#config,
        sessionURL: authenticatedUrl,
      });

      let connection = this.#clientToConnect.getConnection();
      if (!connection || !connection.getSession) {
        throw new Error("Failed to establish rendering connection");
      }
      this.#wslSession = connection.getSession();
      if (!this.#wslSession) {
        throw new Error("Failed to get rendering session");
      }
      this.#remoteSessionId = await this.initRemoteSession();
      this.#setupSubscriptions();
      this.#isConnected = true;

      // Reset retry counter on successful connection
      this.#retryAttempts = 0;

      // Dispatch a custom event to inform listeners that reconnection is complete
      window.dispatchEvent(
        new CustomEvent("wslink:reconnected", { detail: this.#remoteSessionId })
      );
    } catch (error) {
      this.#retryAttempts++;

      if (this.#retryAttempts >= RenderingClient.MAX_RETRY_ATTEMPTS) {
        this.#hasExhaustedRetries = true;
        const exhaustedError = new Error(
          `Failed to connect after ${RenderingClient.MAX_RETRY_ATTEMPTS} attempts. Refresh the page to try again.`
        );
        exhaustedError.retriesExhausted = true;
        exhaustedError.originalError = error;
        this.#notifyConnectionError(exhaustedError);
        throw exhaustedError;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        RenderingClient.INITIAL_RETRY_DELAY_MS *
          Math.pow(2, this.#retryAttempts - 1),
        RenderingClient.MAX_RETRY_DELAY_MS
      );

      // Notify about retry attempt
      this.#notifyConnectionError({
        message: `Connection failed. Retrying (${this.#retryAttempts}/${RenderingClient.MAX_RETRY_ATTEMPTS})...`,
        isRetrying: true,
        attempt: this.#retryAttempts,
        maxAttempts: RenderingClient.MAX_RETRY_ATTEMPTS,
        originalError: error,
      });

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry connection
      return this.connectAsync();
    }
  }

  async initRemoteSession() {
    try {
      const sessionId = await this.#wslSession.call(
        RemoteRenderingActionEnum.InitSession,
        [this.#renderingOptions]
      );

      //assosiate client with session
      await this.associateClientWithSession(sessionId);

      return sessionId;
    } catch (error) {
      throw error;
    }
  }

  setRenderingOptions(renderingOptions = {}) {
    if (!renderingOptions || typeof renderingOptions !== "object") {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(renderingOptions, "zoomSensitivity")) {
      this.#renderingOptions.zoomSensitivity =
        RenderingClient.normalizeZoomSensitivity(
          renderingOptions.zoomSensitivity
        );
    }
  }

  getRenderingOptions() {
    return { ...this.#renderingOptions };
  }

  async setZoomSensitivity(value) {
    const normalizedValue = RenderingClient.normalizeZoomSensitivity(value);
    this.#renderingOptions.zoomSensitivity = normalizedValue;

    if (!this.#wslSession || !this.#remoteSessionId) {
      return;
    }

    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.SetZoomSensitivity, [
        this.#remoteSessionId,
        normalizedValue,
      ]);
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async endRemoteSession() {
    // If already disconnecting, prevent multiple calls
    if (this.#disconnecting) {
      return;
    }

    this.#disconnecting = true;

    try {
      this.#clientToConnect.beginBusy();

      // 1. Stop all animations FIRST (before unregistering streams)
      for (const viewData of this.#viewsList) {
        if (!viewData || !viewData.viewId) continue;

        try {
          if (this.#wslSession) {
            // Stop animation for the specific view
            await this.#wslSession.call("viewport.image.animation.stop", [
              viewData.viewId,
            ]);
          }
        } catch (error) {}
      }

      // 2. Add longer delay to ensure animations are fully stopped
      await new Promise(resolve => setTimeout(resolve, 300));

      // 3. Then unregister all view streams
      await this.unregisterAllViewStreams();

      // 5. Finally, end the session on the server
      if (this.#wslSession && this.#remoteSessionId) {
        try {
          await this.#wslSession.call(RemoteRenderingActionEnum.EndSession, [
            this.#remoteSessionId,
          ]);
        } catch (error) {}
      }
    } catch (error) {
    } finally {
      // Always perform cleanup, even if errors occurred
      if (this.#clientToConnect) {
        this.#clientToConnect.endBusy();
        try {
          this.#clientToConnect.disconnect(-1);
        } catch (error) {}
      }

      // Reset all state
      this.#viewsList = [];
      this.#wslSession = null;
      this.#remoteSessionId = null;
      this.#isConnected = false;
      this.#disconnecting = false;
      this.#surgeryReloadCallbacks.clear();
    }
  }

  // endregion Session --------------------------------------------------------------------------

  // region Common --------------------------------------------------------------------------
  async getBrightnessAndContrast(viewId, key = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetBrightnessAndContrast,
        [this.#remoteSessionId, viewId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setBrightnessAndContrast(viewId, key, brightness, contrast) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(
        RemoteRenderingActionEnum.SetBrightnessAndContrast,
        [this.#remoteSessionId, viewId, key, brightness, contrast]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async resetView(viewId, key = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.ResetView, [
        this.#remoteSessionId,
        viewId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setValidationLock(key, viewId, isLocked) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.SetValidationLock, [
        this.#remoteSessionId,
        key,
        viewId,
        isLocked,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async undoValidation(key, viewId = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.UndoValidation, [
        this.#remoteSessionId,
        key,
        viewId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async redoValidation(key, viewId = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.RedoValidation, [
        this.#remoteSessionId,
        key,
        viewId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async resetValidation(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.ResetValidation, [
        this.#remoteSessionId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getValidationHistoryState(key, viewId = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetValidationHistoryState,
        [this.#remoteSessionId, key, viewId]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  // region Visualization --------------------------------------------------------------------------
  async loadSpinesimVisualizationView(viewData) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      let result = await this.safeRpcCall(
        RemoteRenderingActionEnum.LoadSpinesimVisualizationView,
        [this.#remoteSessionId, viewData]
      );
      this.#viewsList.push(result);
      return result;
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  // endregion Visualization

  // region Surgery --------------------------------------------------------------------------
  // RPC Methods (All use safeRpcCall)
  // --------------------------------------------------------------------------
  async loadImage(filePath, type) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      let result = await this.safeRpcCall(RemoteRenderingActionEnum.LoadImage, [
        this.#remoteSessionId,
        filePath,
        type,
      ]);
      this.#viewsList.push(result);
      return result;
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async changeSliceLevelAsync(sliceAxe, sliceValue) {
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.ChangeSliceLevel, [
        this.#remoteSessionId,
        sliceAxe,
        sliceValue,
      ]);
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async LoadSpinesimViewStage(key, data, step, refreshData) {
    if (!this.#wslSession) {
      return;
    }
    if (this.#viewsList.length) {
      try {
        await this.unregisterAllViewStreams();
      } catch (error) {}
    }
    this.#clientToConnect.beginBusy();
    try {
      let result = await this.safeRpcCall(
        RemoteRenderingActionEnum.LoadSpinesimViewStage,
        [this.#remoteSessionId, key, data, step, refreshData]
      );
      const resultData = JSON.parse(result);
      this.#viewsList = [];

      // resultData is already the array of views, not an object with dx_views/ct_views properties
      if (Array.isArray(resultData)) {
        resultData.forEach(view => {
          this.#viewsList.push(view);
        });
      } else {
        // Fallback for legacy format if backend returns object structure
        const allViews = [
          ...(resultData.dx_views || []),
          ...(resultData.ct_views || []),
          ...(resultData.mri_views || []),
        ];
        allViews.forEach(view => {
          this.#viewsList.push(view);
        });
      }

      this.#surgeryReloadCallbacks.forEach(callback => {
        try {
          callback({ key, step, result: resultData });
        } catch (error) {}
      });

      return result;
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setMeasurementVisibility(key, measurementId, step) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(
        RemoteRenderingActionEnum.SetMeasurementVisibility,
        [this.#remoteSessionId, key, measurementId, step]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setCustomMeasurementVisibility(key, measurementId, step) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(
        RemoteRenderingActionEnum.SetCustomMeasurementVisibility,
        [this.#remoteSessionId, key, measurementId, step]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getVisibleMeasurements(key, step) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetVisibleMeasurements,
        [this.#remoteSessionId, key, step]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async addCustomMeasurement(
    key,
    viewTypes,
    pythonClass,
    category,
    selectedTool,
    measureId,
    dbId,
    firstPointPosition = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.AddCustomMeasurement, [
        this.#remoteSessionId,
        key,
        viewTypes,
        pythonClass,
        category,
        selectedTool,
        measureId,
        dbId,
        firstPointPosition,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeCustomMeasurement(key, measurementId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(
        RemoteRenderingActionEnum.RemoveCustomMeasurement,
        [this.#remoteSessionId, key, measurementId]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setToolVisibility(
    key,
    toolId,
    visibility,
    viewType = null,
    region = null,
    step
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.SetToolVisibility, [
        this.#remoteSessionId,
        key,
        toolId,
        visibility,
        region,
        step,
      ]);
    } catch (error) {
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setValidationToolComponentsVisibility(key, componentsVisibility) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(
        RemoteRenderingActionEnum.SetValidationToolComponentsVisibility,
        [this.#remoteSessionId, key, componentsVisibility]
      );
    } catch (error) {
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async startVertebraSelection(
    key,
    viewType = null,
    tag = VertebraSelectionModality.CUSTOM_MEASUREMENT,
    numberOfSelection = 1
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.StartVertebraSelection, [
        this.#remoteSessionId,
        key,
        viewType,
        tag,
        numberOfSelection,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async stopVertebraSelection(key, viewType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.StopVertebraSelection, [
        this.#remoteSessionId,
        key,
        viewType,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  // region osteotomy
  async showOsteotomyTool(
    key,
    viewType = null,
    vertebraIds = null,
    osteotomyType = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.ShowOsteotomyTool,
        [this.#remoteSessionId, key, vertebraIds, osteotomyType]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async hideOsteotomyTool(key, viewType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.HideOsteotomyTool, [
        this.#remoteSessionId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async updateOsteotomyToolValue(
    key,
    viewType = null,
    vertebraId = null,
    value = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.UpdateOsteotomyToolValue,
        [this.#remoteSessionId, key, vertebraId, value]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getOsteotomySupportValues(key, viewType = null, vertebraId = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetOsteotomySupportValues,
        [this.#remoteSessionId, key, vertebraId]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  //region segmental transformation

  async showSegmentalTransformationTool(
    key,
    viewType = null,
    vertebraId = null,
    vertebraLabel = null,
    type = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.ShowSegmentalTransformationTool,
        [this.#remoteSessionId, key, vertebraId, vertebraLabel, type]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async hideSegmentalTransformationTool(key, viewType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.HideSegmentalTransformationTool,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getSegmentalTransformationAttributes(
    key,
    viewType = null,
    vertebraId = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetSegmentalTransformationAttributes,
        [this.#remoteSessionId, key, vertebraId]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async translateSegmentalTransformationTool(
    key,
    viewType = null,
    direction = TranslationDirections.HORIZONTAL,
    value = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.TranslateSegmentalTransformationTool,
        [this.#remoteSessionId, key, direction, value]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async rotateSegmentalTransformationTool(key, viewType = null, angle = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.RotateSegmentalTransformationTool,
        [this.#remoteSessionId, key, angle]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  //region cage
  async showCageTool(
    key,
    viewType = null,
    vertebraId = null,
    vertebraLabel = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.ShowCageTool, [
        this.#remoteSessionId,
        key,

        vertebraId,
        vertebraLabel,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeCagePreview(key, viewType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.RemoveCagePreview, [
        this.#remoteSessionId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getCageAttributes(key, viewType = null, vertebraId = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetCageAttributes,
        [this.#remoteSessionId, key, vertebraId]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async rotateCageTool(key, viewType = null, angle = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.RotateCageTool, [
        this.#remoteSessionId,
        key,

        angle,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setHeightCageTool(key, viewType = null, value = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.SetHeightCageTool,
        [this.#remoteSessionId, key, value]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setWidthCageTool(key, viewType = null, value = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.SetWidthCageTool,
        [this.#remoteSessionId, key, value]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setCagePosition(key, viewType = null, position = 0) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.SetPositionCageTool,
        [this.#remoteSessionId, key, position]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  //region Rod

  async showRodTool(key, rodsPayload = [], spineType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.ShowRodTool, [
        this.#remoteSessionId,
        key,
        rodsPayload,
        spineType,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeRodPreview(key, viewType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.RemoveRodPreview, [
        this.#remoteSessionId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeRodHardware(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.RemoveRodHardware,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async hideRodHardware(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.HideRodHardware, [
        this.#remoteSessionId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async showRodHardware(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.ShowRodHardware, [
        this.#remoteSessionId,
        key,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeCageHardware(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.RemoveCageHardware,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  //region apply surgical acts and decompensation
  async GetInitialGlobalAlignment(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();

    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetInitialGlobalAlignment,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async UpdateGlobalAlignment(key, decompensationSupport = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.UpdateGlobalAlignment,
        [this.#remoteSessionId, key, decompensationSupport]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async HideGlobalAlignmentTool(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.HideGlobalAlignmentTool,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async StartDecompensation(
    key,
    viewType = null,
    decompensationSupport = null
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.StartDecompensation,
        [this.#remoteSessionId, key, decompensationSupport]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async StartSurgery(key, viewType = null, surgical_acts = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      const surgeryData = surgical_acts;
      return await this.safeRpcCall(RemoteRenderingActionEnum.StartSurgery, [
        this.#remoteSessionId,
        key,

        surgeryData,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async focusOnRegion(key, region = SpineRegions.LOWER_BODY) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.FocusImageOnRegion, [
        this.#remoteSessionId,
        key,
        region,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async focusOnEndplate(key, boneUniqueId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.FocusOnEndplate, [
        this.#remoteSessionId,
        key,
        boneUniqueId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getUpdatedAnnotations(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetUpdatedAnnotations,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getRadioDensityStatistics(key, vertebraeEntityIds) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetRadioDensityStatistics,
        [this.#remoteSessionId, key, vertebraeEntityIds]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setAxialRadiodensityVertebra(key, vertebraEntityId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.SetAxialRadiodensityVertebra,
        [this.#remoteSessionId, key, vertebraEntityId]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setRadiodensityProjectionFilter(key, useColorFilter) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.SetRadiodensityProjectionFilter,
        [this.#remoteSessionId, key, useColorFilter]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async updateRadiodensityCylinders(
    key,
    vertebraeEntityIds,
    cylinderDiameter,
    cylinderHeight
  ) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.UpdateRadiodensityCylinders,
        [
          this.#remoteSessionId,
          key,
          vertebraeEntityIds,
          cylinderDiameter,
          cylinderHeight,
        ]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getAvailableVertebrae(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetAvailableVertebrae,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getDeletableVertebrae(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetDeletableVertebrae,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async addVertebra(key, vertebraId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.AddVertebra, [
        this.#remoteSessionId,
        key,
        vertebraId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeVertebra(key, vertebraId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.RemoveVertebra, [
        this.#remoteSessionId,
        key,
        vertebraId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getAvailableLandmarks(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetAvailableLandmarks,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getDeletableLandmarks(key) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetDeletableLandmarks,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async addLandmark(key, landmarkName, pointId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.AddLandmark, [
        this.#remoteSessionId,
        key,
        landmarkName,
        pointId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeLandmark(key, landmarkName, pointId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.RemoveLandmark, [
        this.#remoteSessionId,
        key,
        landmarkName,
        pointId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async setDrrPointId(key, pointIndex, uniqueId) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.SetDrrPointId, [
        this.#remoteSessionId,
        key,
        pointIndex,
        uniqueId,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async removeDrrPoint(key, pointIndex) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(RemoteRenderingActionEnum.RemoveDrrPoint, [
        this.#remoteSessionId,
        key,
        pointIndex,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getDrrPointsState(key) {
    if (!this.#wslSession) {
      return [];
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetDrrPointsState,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async getFunctionalEndplateOptions(key) {
    if (!this.#wslSession) {
      return [];
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetFunctionalEndplateOptions,
        [this.#remoteSessionId, key]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }
  // endregion Surgery

  // region DATA FUSION
  async ShowCtFusionView(key, step) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.ShowCtFusionView, [
        this.#remoteSessionId,
        key,
        step,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async HideCtFusionView(key, step) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.HideCtFusionView, [
        this.#remoteSessionId,
        key,
        step,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async ChangeCtFusionSlice(key, index, step, viewType = null) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      await this.safeRpcCall(RemoteRenderingActionEnum.ChangeCtFusionSlice, [
        this.#remoteSessionId,
        key,
        index,
        step,
        viewType,
      ]);
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  async GetFusionVisibilityState(key, step) {
    if (!this.#wslSession) {
      return;
    }
    this.#clientToConnect.beginBusy();
    try {
      return await this.safeRpcCall(
        RemoteRenderingActionEnum.GetFusionVisibilityState,
        [this.#remoteSessionId, key, step]
      );
    } catch (error) {
      throw error;
    } finally {
      this.#clientToConnect.endBusy();
    }
  }

  // region Utils --------------------------------------------------------------------------
  // Connection Utilities
  // --------------------------------------------------------------------------
  #connectionError(httpReq, text) {
    const message =
      (httpReq && httpReq.response && httpReq.response.error) || text;
  }

  #connectionInfo(httpReq, text) {
    const message =
      (httpReq && httpReq.response && httpReq.response.error) || text;
  }

  #notifyConnectionError(error) {
    this.#connectionErrorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (e) {
        // Ignore callback errors
      }
    });
  }

  getSession() {
    return this.#wslSession;
  }

  getImageStream() {
    return this.#clientToConnect.getImageStream();
  }

  get viewsList() {
    return this.#viewsList;
  }

  get isConnected() {
    return this.#isConnected;
  }

  get isReconnecting() {
    return this.#isReconnecting;
  }

  get hasExhaustedRetries() {
    return this.#hasExhaustedRetries;
  }

  get retryAttempts() {
    return this.#retryAttempts;
  }

  resetRetryState() {
    this.#retryAttempts = 0;
    this.#hasExhaustedRetries = false;
  }

  addSurgeryReloadListener(callback) {
    if (typeof callback !== "function") {
      return () => {};
    }
    this.#surgeryReloadCallbacks.add(callback);
    return () => {
      this.#surgeryReloadCallbacks.delete(callback);
    };
  }

  addConnectionErrorListener(callback) {
    if (typeof callback !== "function") {
      return () => {};
    }
    this.#connectionErrorCallbacks.add(callback);
    return () => {
      this.#connectionErrorCallbacks.delete(callback);
    };
  }
}

// endregion Utils

export default RenderingClient;
